import { useMemo } from 'react';
import {
  ApiOutlined,
  AreaChartOutlined,
  DatabaseOutlined,
  DollarOutlined,
  InboxOutlined,
  LineChartOutlined,
  NodeIndexOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';

import { Bento, StatTile, Tile, TileBody, TileHeader } from '../../../../components/shared';
import { Empty, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import {
  BieuDoCot,
  BieuDoXepChong,
  ChuGiai,
  MAU_AM,
  MAU_DUONG,
  MAU_TIEN,
  MAU_TOKEN,
  type CotDon,
  type CotXepChong,
} from '../../chung/components/BieuDo';
import { DauChatLuong } from '../../chung/components/DauChatLuong';
import { KhoiLoi } from '../../chung/components/KhoiLoi';
import { useThongKeAi } from '../../chung/hooks/useThongKe';
import type { LocAi } from '../../chung/nghiep-vu/loc-ai';
import { TRONG, doTien, nhanMoc, nhanMocDayDu, soNguyen, tyLe } from '../../chung/nghiep-vu/so-thong-ke';
import { soTien } from '../../chung/nghiep-vu/so-tien';
import { LOAI_TOKEN, type NhomPhanRa } from '../../chung/nghiep-vu/types-thong-ke';
import { kiemBayLoai } from '../nghiep-vu/vet';
import { BangPhanRa } from './BangPhanRa';

/**
 * Tab "Tổng quan" của màn Sử dụng AI — mục 7.11 và 8.2. Port `thong-ke/TongQuanAi.tsx` native.
 *
 * Khoảng thời gian thuộc về khung tab (một khoảng cho cả bốn tab), nên tab này KHÔNG có ô chọn thời gian và không tự
 * lấy `Date.now()`.
 *
 * ── BẢY LÁT PHẢI CỘNG ĐỦ TỔNG, VÀ NGƯỜI ĐỌC PHẢI KIỂM ĐƯỢC ───────────────
 * Bảy loại token là một PHÂN HOẠCH (QĐ-14). Màn này cho người đọc kiểm bằng mắt ở ba chỗ: cột xếp chồng vẽ theo
 * `tong_token` (lệch thì hở một khe ở đỉnh), chú giải in con số từng lát kèm tỷ lệ, và một dòng dưới biểu đồ đặt "tổng
 * bảy lát" cạnh "tổng do server chốt". Lệch thì dòng ấy đỏ lên — đó là bộ chuẩn hoá token đang sai, không phải lỗi
 * hiển thị cần che.
 *
 * Không có chi phí vốn: audit-service không trả cột ấy cho tenant.
 */
export function TongQuanAi({
  tu,
  den,
  loc,
  coLoc = false,
  nhanKhoa,
}: {
  tu: string;
  den: string;
  loc?: LocAi;
  /** Đang có ô lọc — rỗng thì chỉ nói không có gì khớp, không gợi ý mở rộng khoảng. */
  coLoc?: boolean;
  nhanKhoa?: (nhom: NhomPhanRa, khoa: string) => string;
}) {
  const { t, locale } = useT();
  const truyVan = useThongKeAi(tu, den, loc);
  const du = truyVan.data;

  const nhanLoai = useMemo(
    () => LOAI_TOKEN.map((loai) => t(`nktv.loaiToken.${loai}`)),
    // `t` là hàm mới mỗi lần vẽ; nhãn chỉ đổi khi đổi ngôn ngữ.
    [locale],
  );

  const cotToken = useMemo<CotXepChong[]>(() => {
    if (!du) return [];
    return du.theo_thoi_gian.map((hang) => {
      const nhan = nhanMoc(hang.moc, du.buoc_thoi_gian, locale);
      const lat = LOAI_TOKEN.map((loai) => hang[`token_${loai}`]);
      const chiTiet = lat.map((gt, i) => `${nhanLoai[i] ?? ''} ${soNguyen(gt, locale)}`).join(' · ');
      return {
        nhan,
        tieuDe: nhanMocDayDu(hang.moc, du.buoc_thoi_gian, locale),
        lat,
        tong: hang.tong_token,
        moTa: `${nhan} — ${t('nktvSuDungAi.bieuDo.tongToken')} ${soNguyen(hang.tong_token, locale)} · ${chiTiet}`,
      };
    });
  }, [du, nhanLoai, locale]);

  const cotChiPhi = useMemo<CotDon[]>(() => {
    if (!du) return [];
    return du.theo_thoi_gian.map((hang) => {
      const nhan = nhanMoc(hang.moc, du.buoc_thoi_gian, locale);
      const gt = doTien(hang.chi_phi_ban);
      const tien = soTien(gt, hang.don_vi_tien, locale) ?? TRONG;
      return {
        nhan,
        tieuDe: nhanMocDayDu(hang.moc, du.buoc_thoi_gian, locale),
        giaTri: gt ?? 0,
        moTa: `${nhan} — ${tien} · ${t('nktvSuDungAi.bieuDo.soLuot')} ${soNguyen(hang.so_luot, locale)}`,
        the: [
          { nhan: t('nktvSuDungAi.bieuDo.chiPhi'), giaTri: tien, mau: MAU_TIEN },
          { nhan: t('nktvSuDungAi.bieuDo.soLuot'), giaTri: soNguyen(hang.so_luot, locale) },
        ],
      };
    });
  }, [du, locale]);

  const cotTietKiem = useMemo<CotDon[]>(() => {
    if (!du) return [];
    return du.theo_thoi_gian.map((hang) => {
      const nhan = nhanMoc(hang.moc, du.buoc_thoi_gian, locale);
      const gt = doTien(hang.tiet_kiem_dem);
      const tien = soTien(gt, hang.don_vi_tien, locale) ?? TRONG;
      return {
        nhan,
        tieuDe: nhanMocDayDu(hang.moc, du.buoc_thoi_gian, locale),
        giaTri: gt ?? 0,
        moTa: `${nhan} — ${tien}`,
        the: [{ nhan: t('nktvSuDungAi.bieuDo.tietKiemThe'), giaTri: tien, mau: (gt ?? 0) < 0 ? MAU_AM : MAU_DUONG }],
      };
    });
  }, [du, locale]);

  const canhToken = useMemo(() => kiemBayLoai(du), [du]);

  if (truyVan.isPending) {
    return (
      // Khung giữ chỗ nằm trong ô `surface` như bản có dữ liệu: `Skeleton` tô `bg-subtle`, đặt thẳng trên nền trang
      // (cũng `bg-subtle`) thì không thấy gì — trông như màn trắng chứ không như đang tải.
      <div className="space-y-4" aria-busy>
        <Tile className="p-4">
          <Skeleton className="h-16" />
        </Tile>
        <Tile className="p-4">
          <Skeleton className="h-60" />
        </Tile>
        <Tile className="p-4">
          <Skeleton className="h-60" />
        </Tile>
      </div>
    );
  }

  if (truyVan.isError || du === undefined) {
    return (
      <Tile>
        <KhoiLoi loi={truyVan.error} onThuLai={() => void truyVan.refetch()} />
      </Tile>
    );
  }

  const tong = du.tong;

  if (tong.so_luot === 0) {
    return (
      <Tile>
        {coLoc ? (
          <Empty icon={InboxOutlined} title={t('nktvSuDungAi.danhSach.rongLoc')} />
        ) : (
          <Empty
            icon={InboxOutlined}
            title={t('nktvSuDungAi.tongQuan.khongLuotTieuDe')}
            description={t('nktvSuDungAi.tongQuan.khongLuotMoTa')}
          />
        )}
      </Tile>
    );
  }

  const tienTietKiem = doTien(tong.tiet_kiem_dem);
  const amTietKiem = tienTietKiem !== null && tienTietKiem < 0;
  const thongSoCanh = {
    bay: soNguyen(canhToken.bay, locale),
    tong: soNguyen(tong.tong_token, locale),
  };

  return (
    <div className="space-y-4">
      <DauChatLuong
        soChuaCoGia={tong.so_chua_co_gia}
        soThieuTyGia={tong.so_thieu_ty_gia}
        soUocTinh={tong.so_uoc_tinh}
        soCoCanhBao={tong.so_co_canh_bao}
        soLuot={tong.so_luot}
      />

      <Bento>
        <StatTile
          nhan={t('nktvSuDungAi.tongQuan.soLuot')}
          so={soNguyen(tong.so_luot, locale)}
          sub={t('nktvSuDungAi.tongQuan.soLuotPhu', { trace: soNguyen(tong.so_trace, locale) })}
          icon={<ThunderboltOutlined />}
          tone="sky"
        />
        <StatTile
          nhan={t('nktvSuDungAi.tongQuan.tongToken')}
          so={soNguyen(tong.tong_token, locale)}
          sub={t('nktvSuDungAi.tongQuan.tongTokenPhu')}
          icon={<DatabaseOutlined />}
          tone="teal"
        />
        <StatTile
          nhan={t('nktvSuDungAi.tongQuan.chiPhi')}
          so={soTien(tong.chi_phi_ban, tong.don_vi_tien, locale) ?? TRONG}
          sub={
            tong.so_chua_co_gia > 0
              ? t('nktvSuDungAi.tongQuan.chiPhiChuaDu', { so: soNguyen(tong.so_chua_co_gia, locale) })
              : tong.chi_phi_von !== undefined
                ? t('nktvSuDungAi.tongQuan.chiPhiVon', {
                    tien: soTien(tong.chi_phi_von, tong.don_vi_tien, locale) ?? TRONG,
                  })
                : t('nktvSuDungAi.tongQuan.chiPhiPhu')
          }
          icon={<DollarOutlined />}
          tone={tong.so_chua_co_gia > 0 ? 'warning' : 'primary'}
        />
        <StatTile
          nhan={t('nktvSuDungAi.tongQuan.soMoHinh')}
          so={soNguyen(tong.so_mo_hinh, locale)}
          sub={t('nktvSuDungAi.tongQuan.soMoHinhPhu')}
          icon={<ApiOutlined />}
          tone="violet"
        />
      </Bento>

      <Tile>
        <TileHeader
          icon={<AreaChartOutlined />}
          tone="teal"
          title={t('nktvSuDungAi.bieuDo.tokenTieuDe')}
          mo={t('nktvSuDungAi.bieuDo.tokenMoTa')}
        />
        <TileBody>
          <BieuDoXepChong
            cot={cotToken}
            loat={LOAI_TOKEN.map((loai, i) => ({ nhan: nhanLoai[i] ?? loai, mau: MAU_TOKEN[loai] }))}
            nhan={t('nktvSuDungAi.bieuDo.tokenNhan')}
            nhanTong={t('nktvSuDungAi.bieuDo.tongToken')}
            dinhDang={(gt) => t('nktvSuDungAi.bieuDo.soTokenThe', { so: soNguyen(gt, locale) })}
          />
          <ChuGiai
            muc={LOAI_TOKEN.map((loai, i) => ({
              nhan: nhanLoai[i] ?? loai,
              mau: MAU_TOKEN[loai],
              phu: `${soNguyen(tong[`token_${loai}`], locale)} · ${tyLe(tong[`token_${loai}`], tong.tong_token, locale)}`,
              moTa: t(`nktv.loaiTokenMoTa.${loai}`),
            }))}
          />
          <p
            className={
              canhToken.khop
                ? 'mt-3 text-xs leading-relaxed text-content-muted'
                : 'mt-3 text-xs font-medium leading-relaxed text-danger-700'
            }
            role={canhToken.khop ? undefined : 'alert'}
          >
            {canhToken.khop
              ? t('nktvSuDungAi.bieuDo.tokenKhop', thongSoCanh)
              : t('nktvSuDungAi.bieuDo.tokenLech', { ...thongSoCanh, moc: soNguyen(canhToken.soMocLech, locale) })}
          </p>
        </TileBody>
      </Tile>

      <Bento>
        <Tile span="lg:col-span-6">
          <TileHeader
            icon={<LineChartOutlined />}
            tone="primary"
            title={t('nktvSuDungAi.bieuDo.chiPhiTieuDe')}
            mo={t('nktvSuDungAi.bieuDo.chiPhiMoTa')}
          />
          <TileBody>
            <BieuDoCot cot={cotChiPhi} nhan={t('nktvSuDungAi.bieuDo.chiPhiNhan')} mau={MAU_TIEN} />
            <p className="mt-3 text-xs leading-relaxed text-content-muted">
              {tong.so_chua_co_gia > 0
                ? t('nktvSuDungAi.bieuDo.chiPhiChuaDu', { so: soNguyen(tong.so_chua_co_gia, locale) })
                : t('nktvSuDungAi.bieuDo.chiPhiGhiChu')}
            </p>
          </TileBody>
        </Tile>

        <Tile span="lg:col-span-6">
          <TileHeader
            icon={<NodeIndexOutlined />}
            tone={amTietKiem ? 'danger' : 'success'}
            title={t('nktvSuDungAi.bieuDo.tietKiemTieuDe')}
            mo={t('nktvSuDungAi.bieuDo.tietKiemMoTa')}
          />
          <TileBody>
            <p
              className={
                amTietKiem
                  ? 'text-xl font-semibold tabular-nums text-danger-700'
                  : 'text-xl font-semibold tabular-nums text-success-700'
              }
            >
              {soTien(tienTietKiem, tong.don_vi_tien, locale) ?? TRONG}
            </p>
            <BieuDoCot
              cot={cotTietKiem}
              nhan={t('nktvSuDungAi.bieuDo.tietKiemNhan')}
              mau={MAU_DUONG}
              mauAm={MAU_AM}
              cao={150}
            />
            <p className="mt-3 text-xs leading-relaxed text-content-muted">{t('nktvSuDungAi.bieuDo.tietKiemAm')}</p>
            <p className="mt-1 text-xs leading-relaxed text-content-muted">
              {t('nktvSuDungAi.bieuDo.tietKiemPhiLuu')}
            </p>
          </TileBody>
        </Tile>
      </Bento>

      <BangPhanRa thongKe={du} nhanKhoa={nhanKhoa} />
    </div>
  );
}
